<template>
  <TransitionRoot
    v-if="guide !== null"
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 mt-20 overflow-hidden"
      @close="open = false"
    >
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen mt-16 max-w-2xl">
              <div class="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div class="px-4 py-6 sm:px-6">
                  <div class="flex items-start justify-between">
                    <DialogTitle class="text-lg font-medium text-gray-900">
                      Profile
                    </DialogTitle>
                    <div class="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500"
                        @close="open = false"
                      >
                        <span class="sr-only">Close panel</span>
                        <XMarkIcon
                          class="h-6 w-6"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Main -->
                <div class="divide-y divide-gray-200">
                  <div class="pb-6">
                    <div class="h-12 sm:h-20 lg:h-6" />
                    <div class="lg:-mt-15 -mt-12 flow-root px-4 sm:-mt-8 sm:flex sm:items-end sm:px-6">
                      <div>
                        <div class="-m-1 flex">
                          <div class="inline-flex overflow-hidden rounded-lg border-4 border-white">
                            <img
                              class=" flex-shrink-0 h-48 w-48"
                              :src="guide.user.profile_photo_url"
                              :alt="guide.user.name"
                            >
                          </div>
                        </div>
                      </div>
                      <div class="mt-6 sm:ml-6 sm:flex-1">
                        <div>
                          <div class="flex items-center">
                            <h3 class="text-xl font-bold text-gray-900 sm:text-2xl">
                              {{ guide.user.name }}
                            </h3>
                            <span class="ml-2.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-green-400">
                              <span class="sr-only">Online</span>
                            </span>
                          </div>
                          <p class="text-sm text-gray-500">
                            {{ guide.team.name }}
                          </p>
                        </div>
                        <div class="mt-5 flex flex-wrap space-y-3 sm:space-y-0 sm:space-x-3">
                          <button
                            type="button"
                            class="inline-flex w-full flex-shrink-0 items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:flex-1"
                          >
                            Message
                          </button>
                          <button
                            type="button"
                            class="inline-flex w-full flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          >
                            Book
                          </button>
                          <span class="ml-3 inline-flex sm:ml-0">
                            <Menu
                              as="div"
                              class="relative inline-block text-left"
                            >
                              <MenuButton class="inline-flex items-center rounded-md border border-gray-300 bg-white p-2 text-sm font-medium text-gray-400 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                <span class="sr-only">Open options menu</span>
                                <EllipsisVerticalIcon
                                  class="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </MenuButton>
                              <transition
                                enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95"
                              >
                                <MenuItems class="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  <div class="py-1">
                                    <MenuItem v-slot="{ active }">
                                      <a
                                        href="#"
                                        :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']"
                                      >View profile</a>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                      <a
                                        href="#"
                                        :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']"
                                      >Copy profile link</a>
                                    </MenuItem>
                                  </div>
                                </MenuItems>
                              </transition>
                            </Menu>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="px-4 py-5 sm:px-0 sm:py-0">
                    <dl class="space-y-8 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                      <div class="sm:flex sm:px-6 sm:py-5">
                        <dt class="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0 lg:w-48">
                          Destinations
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">
                          <ul
                            role="list"
                            class="divide-y divide-gray-200"
                          >
                            <li
                              v-for="place in guide.places"
                              class="py-4 flex"
                            >
                              <div class="flex flex-grow justify-between">
                                <div class="ml-0">
                                  <p class="text-sm font-medium text-gray-900">
                                    {{ place.name }}
                                  </p>
                                  <div class="flex space-x-2 items-center">
                                    <img
                                      :src="place.image"
                                      class="h-3"
                                      :alt="place.area"
                                    >
                                    <span class="text-sm text-gray-500">
                                      {{ place.area }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </dd>
                      </div>
                      <div class="sm:flex sm:px-6 sm:py-5">
                        <dt class="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0 lg:w-48">
                          Languages
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">
                          <ul
                            role="list"
                            class="divide-y divide-gray-200"
                          >
                            <li
                              v-for="language in guide.languages"
                              class="py-2 flex"
                            >
                              <img
                                class="h-4 mt-1.5"
                                :src="language.image"
                                alt=""
                              >
                              <div class="flex flex-grow justify-between">
                                <div class="ml-3">
                                  <p class="text-sm font-medium text-gray-900">
                                    {{ language.name }}
                                  </p>
                                  <p class="text-xs text-gray-500">
                                    {{ language.native }}
                                  </p>
                                </div>
                              </div>
                            </li>
                          </ul>
                        </dd>
                      </div>
                      <div class="sm:flex sm:px-6 sm:py-5">
                        <dt class="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0 lg:w-48">
                          Experience
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">
                          {{ guide.experience }}
                        </dd>
                      </div>
                      <div class="sm:flex sm:px-6 sm:py-5">
                        <dt class="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0 lg:w-48">
                          Knowledge
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">
                          {{ guide.knowledge }}
                        </dd>
                      </div>
                      <div class="sm:flex sm:px-6 sm:py-5">
                        <dt class="text-sm font-medium text-gray-500 sm:w-40 sm:flex-shrink-0 lg:w-48">
                          Tour Types
                        </dt>
                        <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 sm:ml-6">
                          {{ guide.tour_types }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import { ref } from 'vue';
import {
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid';

export default {
  components: {
    Dialog,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
    EllipsisVerticalIcon,
    XMarkIcon,
  },
  props: {
    guide: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const open = ref(true);

    return {
      open,
    };
  },
};
</script>
